(() => {
  if (window.RealmHeaderFidelity) return;
  const active = new Map();
  const desktop = window.matchMedia('(min-width: 1025px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  function mount(header) {
    const menu = header.closest('.realm-header-section')?.querySelector('[data-realm-menu]') || document.getElementById('RealmMenu');
    if (!menu || active.has(header)) return;
    header.dataset.realmHeaderBound = 'true';
    let destroyed = false;
    const listeners = [];
    function listen(target, type, handler, options) {
      if (!target) return;
      target.addEventListener(type, handler, options);
      listeners.push(() => target.removeEventListener(type, handler, options));
    }
    const sections = Array.from(menu.querySelectorAll('[data-menu-tab]')).map(button => ({ button, panel: document.getElementById(button.getAttribute('aria-controls')), open: false }));
    const callers = Array.from(header.querySelectorAll('[data-menu-start]'));
    const mobileCaller = callers.find(entry => !entry.closest('.realm-desktop-nav'));
    let selectedKey = 'shop';
    let caller = null;
    let desktopOpen = false;
    let mobileOpen = false;
    let freezeContext = null;
    let closeTimer = null;
    let categoryPin = null;
    let categoryOffset = null;
    function pinCategories() {
      const strip = document.body.classList.contains('realm-page--collection') ? document.querySelector('.realm-collection-categories') : null;
      if (!strip) return;
      categoryPin = { element: strip, alreadyPinned: strip.classList.contains('realm-collection-categories--menu-pinned') };
      if (!categoryOffset) categoryOffset = [menu.style.getPropertyValue('--realm-mobile-category-height'), menu.style.getPropertyPriority('--realm-mobile-category-height')];
      menu.style.setProperty('--realm-mobile-category-height', strip.getBoundingClientRect().height + 'px');
      menu.classList.add('realm-menu--with-categories');
      strip.classList.add('realm-collection-categories--menu-pinned');
    }
    function unpinCategories() {
      if (categoryPin && !categoryPin.alreadyPinned) categoryPin.element.classList.remove('realm-collection-categories--menu-pinned');
      categoryPin = null;
    }
    function clearCategoryOffset() {
      menu.classList.remove('realm-menu--with-categories');
      if (categoryOffset?.[0]) menu.style.setProperty('--realm-mobile-category-height', ...categoryOffset);
      else menu.style.removeProperty('--realm-mobile-category-height');
      categoryOffset = null;
    }
    function backgroundInertTargets() {
      const main = document.getElementById('MainContent');
      const footer = document.querySelector('[data-realm-footer-group]') || document.querySelector('[data-realm-footer]');
      const targets = new Set(footer ? [footer] : []);
      const strip = categoryPin?.element;
      if (main && strip && main.contains(strip)) {
        let allowed = strip;
        let ancestor = allowed.parentElement;
        while (ancestor && main.contains(ancestor)) {
          Array.from(ancestor.children).forEach(child => { if (child !== allowed) targets.add(child); });
          if (ancestor === main) break;
          allowed = ancestor;
          ancestor = ancestor.parentElement;
        }
      } else if (main) targets.add(main);
      return Array.from(targets);
    }
    function refreshState() {
      const requestedOpen = desktop.matches ? desktopOpen : mobileOpen;
      callers.forEach(entry => {
        const expanded = requestedOpen && (!desktop.matches || entry.dataset.menuStart === selectedKey);
        entry.setAttribute('aria-expanded', String(expanded));
        if (entry.dataset.menuOpenLabel) entry.setAttribute('aria-label', expanded ? entry.dataset.menuCloseLabel : entry.dataset.menuOpenLabel);
        if (entry.closest('.realm-desktop-nav')) {
          if (!entry.dataset.closedLabel) entry.dataset.closedLabel = entry.textContent;
          entry.textContent = expanded ? entry.dataset.closedLabel.replace(/\+\s*$/, '−') : entry.dataset.closedLabel;
        }
      });
      const interactionOpen = requestedOpen || Array.from(document.querySelectorAll('#RealmSearch,#RealmCartDrawer')).some(dialog => dialog.open);
      header.classList.toggle('realm-header--interaction', interactionOpen);
      syncHeroContrast();
    }
    function selectDesktop(key) {
      selectedKey = sections.some(entry => entry.button.dataset.menuTab === key) ? key : 'shop';
      menu.dataset.menuSelection = selectedKey;
      sections.forEach(entry => {
        const active = entry.button.dataset.menuTab === selectedKey;
        // Keep closed lists rendered so native CSS transitions can reverse from
        // their current state, as they do in the reference's height-zero panels.
        entry.panel.hidden = false;
        entry.panel.inert = !active;
        entry.panel.setAttribute('aria-hidden', String(!active));
        for (const property of ['height', 'padding', 'overflow']) {
          if (active) entry.panel.style.removeProperty(property);
          else entry.panel.style.setProperty(property, property === 'overflow' ? 'hidden' : '0px');
        }
        entry.panel.removeAttribute('data-mobile-open');
      });
      const title = document.getElementById('RealmMenuTitle');
      if (title) title.textContent = sections.find(entry => entry.button.dataset.menuTab === selectedKey)?.button.firstChild.textContent.trim() || title.textContent;
    }
    function configureDesktopLists() {
      sections.forEach(entry => {
        const list = entry.panel.querySelector('[data-menu-list]');
        if (!list) return;
        list.style.transition = reducedMotion.matches ? 'none' : 'opacity .22s ease .08s,transform .22s ease .08s';
        list.style.opacity = '0';
        list.style.transform = 'translateY(24px)';
      });
    }
    function targetDesktopLists() {
      sections.forEach(entry => {
        const list = entry.panel.querySelector('[data-menu-list]');
        if (!list) return;
        const visible = desktopOpen && entry.button.dataset.menuTab === selectedKey;
        list.style.opacity = visible ? '1' : '0';
        list.style.transform = visible ? 'translateY(0)' : 'translateY(24px)';
      });
    }
    function clearDesktopShell() {
      menu.inert = false;
      menu.removeAttribute('aria-hidden');
      for (const property of ['height', 'min-height', 'overflow']) menu.style.removeProperty(property);
    }
    function prepareMobileSections() {
      sections.forEach(entry => {
        for (const property of ['padding', 'overflow']) entry.panel.style.removeProperty(property);
        const list = entry.panel.querySelector('[data-menu-list]');
        if (list) for (const property of ['transition', 'opacity', 'transform']) list.style.removeProperty(property);
        entry.panel.hidden = !entry.open;
        entry.panel.inert = !entry.open;
        entry.panel.setAttribute('aria-hidden', String(!entry.open));
        entry.panel.dataset.mobileOpen = String(entry.open);
        entry.panel.style.height = entry.open ? 'auto' : '0px';
        entry.button.setAttribute('aria-expanded', String(entry.open));
        entry.button.querySelector('span').textContent = entry.open ? '−' : '+';
      });
    }
    function freezePage() {
      if (freezeContext) return;
      const body = document.body;
      const styles = {};
      for (const key of ['position', 'top', 'left', 'right', 'width', 'overflow']) styles[key] = [body.style.getPropertyValue(key), body.style.getPropertyPriority(key)];
      const inert = backgroundInertTargets().map(element => ({ element, value: element.inert }));
      const width = body.getBoundingClientRect().width;
      freezeContext = { x: window.scrollX, y: window.scrollY, styles, inert, viewportInset: Math.max(0, window.innerWidth - width) };
      body.style.position = 'fixed';
      body.style.top = -freezeContext.y + 'px';
      body.style.left = -freezeContext.x + 'px';
      body.style.right = 'auto';
      body.style.width = width + 'px';
      body.style.overflow = 'hidden';
      inert.forEach(entry => { entry.element.inert = true; });
    }
    function syncFrozenPageWidth() {
      // Body locking removes the scrollbar. Keep its opening inset while the
      // responsive menu is open instead of retaining a stale phone width.
      if (freezeContext) document.body.style.width = Math.max(0, window.innerWidth - freezeContext.viewportInset) + 'px';
    }
    function unfreezePage() {
      if (!freezeContext) return;
      const context = freezeContext;
      freezeContext = null;
      Object.entries(context.styles).forEach(([key, [value, priority]]) => { if (value) document.body.style.setProperty(key, value, priority); else document.body.style.removeProperty(key); });
      context.inert.forEach(entry => { entry.element.inert = entry.value; });
      unpinCategories();
      const style = document.documentElement.style;
      const previous = [style.getPropertyValue('scroll-behavior'), style.getPropertyPriority('scroll-behavior')];
      style.setProperty('scroll-behavior', 'auto');
      window.scrollTo(context.x, context.y);
      requestAnimationFrame(() => { if (previous[0]) style.setProperty('scroll-behavior', ...previous); else style.removeProperty('scroll-behavior'); });
    }
    function finishClose() {
      clearTimeout(closeTimer);
      closeTimer = null;
      if (!mobileOpen && !desktopOpen) {
        if (menu.open) menu.close();
        clearDesktopShell();
        clearCategoryOffset();
      }
    }
    function closeMenu({ immediate = false, restoreFocus = false } = {}) {
      if (!caller && !mobileOpen && !menu.open) return;
      const previousCaller = caller;
      caller = null;
      desktopOpen = false;
      mobileOpen = false;
      menu.classList.remove('realm-menu--visible');
      // A fading nonmodal dialog must stop receiving focus immediately.
      // Otherwise a quick header action can be caught by its old focus scope.
      menu.inert = true;
      menu.setAttribute('aria-hidden', 'true');
      unfreezePage();
      refreshState();
      if (restoreFocus && previousCaller?.isConnected) previousCaller.focus({ preventScroll: true });
      clearTimeout(closeTimer);
      if (desktop.matches) {
        targetDesktopLists();
        // Reference auto-height menus collapse immediately. Preserve the
        // rendered, inaccessible lists until their native transition settles.
        menu.style.height = '0px';
        menu.style.setProperty('min-height', '0px');
        menu.style.overflow = 'hidden';
        menu.inert = true;
        menu.setAttribute('aria-hidden', 'true');
        if (reducedMotion.matches) finishClose();
        else closeTimer = setTimeout(finishClose, 300);
      } else if (immediate || reducedMotion.matches) finishClose();
      else closeTimer = setTimeout(finishClose, 240);
    }
    function openDesktop(entry, keyboard = false) {
      if (caller === entry && desktopOpen) {
        if (keyboard) sections.find(section => section.button.dataset.menuTab === selectedKey)?.panel.querySelector('a')?.focus({ preventScroll: true });
        else closeMenu();
        return;
      }
      document.dispatchEvent(new CustomEvent('realm:overlay-opening'));
      window.RealmAppStories?.closeForOverlay?.();
      window.RealmAppReviews?.closeForOverlay?.();
      clearTimeout(closeTimer);
      closeTimer = null;
      selectDesktop(entry.dataset.menuStart);
      caller = entry;
      desktopOpen = true;
      clearDesktopShell();
      if (!menu.open) menu.show();
      menu.getBoundingClientRect();
      targetDesktopLists();
      refreshState();
      const panel = sections.find(section => section.button.dataset.menuTab === selectedKey)?.panel;
      if (keyboard) panel?.querySelector('a,button')?.focus({ preventScroll: true });
      else entry.focus({ preventScroll: true });
    }
    function openMobile(entry) {
      if (mobileOpen) { closeMenu({ restoreFocus: true }); return; }
      document.dispatchEvent(new CustomEvent('realm:overlay-opening'));
      window.RealmAppStories?.closeForOverlay?.();
      window.RealmAppReviews?.closeForOverlay?.();
      clearTimeout(closeTimer);
      closeTimer = null;
      caller = entry;
      mobileOpen = true;
      clearDesktopShell();
      prepareMobileSections();
      pinCategories();
      freezePage();
      if (!menu.open) menu.show();
      menu.getBoundingClientRect();
      menu.classList.add('realm-menu--visible');
      refreshState();
      entry.focus({ preventScroll: true });
    }
    function toggleSection(entry) {
      const height = entry.panel.hidden ? 0 : entry.panel.getBoundingClientRect().height;
      entry.open = !entry.open;
      entry.panel.hidden = false;
      entry.panel.inert = !entry.open;
      entry.panel.setAttribute('aria-hidden', String(!entry.open));
      entry.panel.style.height = height + 'px';
      entry.panel.dataset.mobileOpen = String(entry.open);
      entry.button.setAttribute('aria-expanded', String(entry.open));
      entry.button.querySelector('span').textContent = entry.open ? '−' : '+';
      entry.panel.getBoundingClientRect();
      entry.panel.style.height = entry.open ? entry.panel.scrollHeight + 'px' : '0px';
      if (reducedMotion.matches) { entry.panel.hidden = !entry.open; if (entry.open) entry.panel.style.height = 'auto'; }
    }
    sections.forEach(entry => listen(entry.panel, 'transitionend', event => {
      if (desktop.matches || event.target !== entry.panel || event.propertyName !== 'height') return;
      entry.panel.hidden = !entry.open;
      if (entry.open) entry.panel.style.height = 'auto';
    }));
    sections.forEach(entry => listen(entry.panel.querySelector('[data-menu-list]'), 'transitionend', event => {
      if (desktop.matches && !desktopOpen && entry.button.dataset.menuTab === selectedKey && event.target === entry.panel.querySelector('[data-menu-list]') && event.propertyName === 'opacity' && Number(getComputedStyle(event.target).opacity) === 0) finishClose();
    }));
    listen(menu, 'transitionend', event => { if (!desktop.matches && event.target === menu && event.propertyName === 'opacity' && !mobileOpen) finishClose(); });
    listen(document, 'click', event => {
      const entry = event.target.closest('[data-menu-start]');
      if (entry && header.contains(entry)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (desktop.matches) openDesktop(entry, event.detail === 0);
        else openMobile(entry);
        return;
      }
      const section = sections.find(item => item.button === event.target.closest('[data-menu-tab]'));
      if (section && !desktop.matches) { event.preventDefault(); event.stopImmediatePropagation(); toggleSection(section); return; }
      if (event.target.closest('[data-dialog-open],[data-cart-open]') && (desktopOpen || mobileOpen)) { closeMenu({ immediate: true }); return; }
      if ((desktopOpen || mobileOpen) && event.target.closest('[data-dialog-close]') && menu.contains(event.target)) { event.preventDefault(); event.stopImmediatePropagation(); closeMenu({ restoreFocus: true }); return; }
      if ((desktopOpen || mobileOpen) && event.target.closest('a[href]')) closeMenu({ immediate: true });
      else if ((desktopOpen || mobileOpen) && !header.contains(event.target) && !menu.contains(event.target) && !categoryPin?.element.contains(event.target)) closeMenu();
    }, true);
    listen(document, 'keydown', event => {
      if ((caller || mobileOpen) && event.key === 'Escape') { event.preventDefault(); event.stopImmediatePropagation(); closeMenu({ restoreFocus: true }); return; }
      const entry = event.target.closest('.realm-desktop-nav [data-menu-start]');
      if (desktop.matches && entry && event.key === 'ArrowDown') { event.preventDefault(); openDesktop(entry, true); return; }
      if (!mobileOpen || event.key !== 'Tab') return;
      const list = [header, menu, categoryPin?.element].filter(Boolean).flatMap(island => Array.from(island.querySelectorAll('button:not([disabled]),a[href],input:not([disabled]),[tabindex="0"]'))).filter(element => !element.closest('[inert]') && element.getClientRects().length);
      const first = list[0], last = list[list.length - 1];
      if (event.shiftKey && (document.activeElement === first || !list.includes(document.activeElement))) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && (document.activeElement === last || !list.includes(document.activeElement))) { event.preventDefault(); first?.focus(); }
    }, true);
    listen(document, 'focusin', event => {
      if (!header.contains(event.target) && !menu.contains(event.target) && !categoryPin?.element.contains(event.target)) {
        if (mobileOpen) mobileCaller?.focus({ preventScroll: true });
        else if (caller) closeMenu();
      }
    });
    const observer = new MutationObserver(refreshState);
    [menu, document.getElementById('RealmSearch'), document.getElementById('RealmCartDrawer')].filter(Boolean).forEach(dialog => observer.observe(dialog, { attributes: true, attributeFilter: ['open'] }));
    listen(menu, 'cancel', event => { event.preventDefault(); closeMenu({ restoreFocus: true }); });
    listen(menu, 'close', () => { if (menu.open) return; caller = null; desktopOpen = false; mobileOpen = false; clearDesktopShell(); unfreezePage(); refreshState(); });
    function syncHeroContrast() {
      if (destroyed || header.isConnected === false) return;
      if (!header.classList.contains('realm-header--home')) return;
      const hero = document.querySelector('.realm-hero');
      const bounds = hero?.getBoundingClientRect();
      const headerBounds = header.getBoundingClientRect();
      // Transparent only while the hero actually sits behind the whole header.
      // Editor sections placed above the hero retain readable glass contrast.
      header.classList.toggle('realm-header--past-hero', !bounds || bounds.top > headerBounds.top || bounds.bottom <= headerBounds.bottom);
    }
    let heroFrame = null;
    function scheduleHeroContrast() {
      if (destroyed || heroFrame !== null) return;
      heroFrame = requestAnimationFrame(() => { heroFrame = null; syncHeroContrast(); });
    }
    let heroResizeObserver = null;
    let heroRefreshFrame = null;
    function observeHeroBounds() {
      if (!header.classList.contains('realm-header--home') || typeof ResizeObserver !== 'function') return;
      if (!heroResizeObserver) heroResizeObserver = new ResizeObserver(scheduleHeroContrast);
      heroResizeObserver.disconnect();
      heroResizeObserver.observe(header);
      const hero = document.querySelector('.realm-hero');
      if (hero) heroResizeObserver.observe(hero);
    }
    function revealActiveCategory() {
      if (desktop.matches) return;
      const strip = document.querySelector('.realm-collection-categories');
      const active = strip?.querySelector('[aria-current="page"]');
      if (!strip || !active) return;
      requestAnimationFrame(() => {
        if (destroyed || strip.isConnected === false) return;
        const target = strip.scrollLeft + active.getBoundingClientRect().left - strip.getBoundingClientRect().left - parseFloat(getComputedStyle(strip).paddingLeft || '0');
        strip.scrollLeft = Math.max(0, Math.min(target, strip.scrollWidth - strip.clientWidth));
      });
    }
    function syncMobileSubmenuHeights() {
      if (desktop.matches) return;
      sections.forEach(entry => {
        if (entry.open && entry.panel.style.height !== 'auto') entry.panel.style.height = entry.panel.scrollHeight + 'px';
      });
    }
    listen(window, 'scroll', syncHeroContrast, { passive: true });
    // Release menu ownership before the browser caches this document. Returning
    // with Back/Forward must not revive a fixed body or an inert content tree.
    listen(window, 'pagehide', () => { closeMenu({ immediate: true }); finishClose(); });
    listen(window, 'pageshow', () => { closeMenu({ immediate: true }); finishClose(); refreshState(); observeHeroBounds(); scheduleHeroContrast(); });
    listen(window, 'load', syncHeroContrast);
    listen(window, 'resize', () => { syncFrozenPageWidth(); syncHeroContrast(); revealActiveCategory(); syncMobileSubmenuHeights(); });
    listen(document, 'shopify:section:load', () => { observeHeroBounds(); syncHeroContrast(); });
    listen(document, 'shopify:section:reorder', () => { observeHeroBounds(); syncHeroContrast(); });
    listen(document, 'shopify:section:unload', () => {
      // Unload fires before Shopify removes the old section from the DOM.
      // Reconnect after removal so a deleted hero releases white controls.
      if (heroRefreshFrame !== null) cancelAnimationFrame(heroRefreshFrame);
      heroRefreshFrame = requestAnimationFrame(() => { heroRefreshFrame = null; if (!destroyed) { observeHeroBounds(); syncHeroContrast(); } });
    });
    listen(document, 'realm:collection-updated', revealActiveCategory);
    listen(desktop, 'change', () => { closeMenu({ immediate: true }); finishClose(); if (desktop.matches) { configureDesktopLists(); selectDesktop(selectedKey); } else prepareMobileSections(); refreshState(); });
    listen(reducedMotion, 'change', () => {
      if (desktop.matches) {
        sections.forEach(entry => { const list = entry.panel.querySelector('[data-menu-list]'); if (list) list.style.transition = reducedMotion.matches ? 'none' : 'opacity .22s ease .08s,transform .22s ease .08s'; });
        targetDesktopLists();
      }
      if (reducedMotion.matches && !mobileOpen && !desktopOpen) finishClose();
    });
    if (desktop.matches) { configureDesktopLists(); selectDesktop('shop'); }
    else prepareMobileSections();
    refreshState();
    syncHeroContrast();
    observeHeroBounds();
    document.fonts?.ready?.then(() => { if (!destroyed) syncHeroContrast(); });
    revealActiveCategory();
    active.set(header, { destroy() {
      closeMenu({ immediate: true });
      finishClose();
      unfreezePage();
      unpinCategories();
      clearCategoryOffset();
      destroyed = true;
      if (heroFrame !== null) cancelAnimationFrame(heroFrame);
      heroFrame = null;
      if (heroRefreshFrame !== null) cancelAnimationFrame(heroRefreshFrame);
      heroRefreshFrame = null;
      heroResizeObserver?.disconnect();
      observer.disconnect();
      listeners.splice(0).forEach(remove => remove());
      delete header.dataset.realmHeaderBound;
      active.delete(header);
    } });
  }
  function initialize(scope = document) {
    for (const [header, controller] of active) if (header.isConnected === false) controller.destroy();
    const headers = Array.from(scope.querySelectorAll?.('[data-realm-header]') || []);
    if (scope.matches?.('[data-realm-header]')) headers.unshift(scope);
    headers.forEach(mount);
  }
  function unload(event) {
    for (const [header, controller] of active) if (event.target === header || event.target.contains(header)) controller.destroy();
  }
  const ready = () => initialize();
  const load = event => initialize(event.target);
  window.RealmHeaderFidelity = { initialize, destroy() {
    for (const controller of Array.from(active.values())) controller.destroy();
    document.removeEventListener('DOMContentLoaded', ready);
    document.removeEventListener('shopify:section:load', load);
    document.removeEventListener('shopify:section:unload', unload);
    delete window.RealmHeaderFidelity;
  } };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ready, { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', load);
  document.addEventListener('shopify:section:unload', unload);
})();
